import { IUser, IGroup, GroupType, UserRole, DeviceType } from '@/types';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { db } from '@/store/firebase.store';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { useNotificationsStore } from './notifications.store';
import axios from 'axios';
import { useAuthStore } from './auth.store';

const rawDataToGroup = (data:any, id: string):IGroup => ({
  id,
  name: String(data?.name || ''),
  img: String(data?.img || ''),
  type: String(data?.type || '') as GroupType,
  root: String(data?.root || ''),
  activeSubscribers: +data?.activeSubscribers || 0,
  created: +data?.created || 0,
  reportUploadedDate: +data?.reportUploadedDate || 0,
  active: !!data?.active,
  email: String(data?.email || ''),
  phone: String(data?.phone || ''),
  deviceOption: String(data?.deviceOption || '') as DeviceType,
  billing: {
    name: String(data?.billing?.name || ''),
    middleName: String(data?.billing?.middleName || ''),
    lastName: String(data?.billing?.lastName || ''),
    department: String(data?.billing?.department || ''),
    title: String(data?.billing?.title || ''),
    phone: String(data?.billing?.phone || ''),
    email: String(data?.billing?.email || ''),
    billingEmail: String(data?.billing?.billingEmail || ''),
  }
});

const rawDataToUser = (data:any, id: string):IUser => ({
  id,
  img: String(data?.img || ''),
  email: String(data?.email || ''),
  name: String(data?.name || ''),
  phone: String(data?.phone || ''),
  middleName: String(data?.middleName || ''),
  lastName: String(data?.lastName || ''),
  corporateId: String(data?.corporateId || ''),
  role: String(data?.role || '') as UserRole,
  group: String(data?.group || ''), 
  active: !!data?.active,
  permissions: {
    analytics: !!data?.permissions?.analytics,
    admin: !!data?.permissions?.admin,
    сompanyProfile: !!data?.permissions?.сompanyProfile,
    marketing: !!data?.permissions?.marketing,
    uploadData: !!data?.permissions?.uploadData,
    subscriberList: !!data?.permissions?.subscriberList,
    exportData: !!data?.permissions?.exportData,
    manageTeam: !!data?.permissions?.manageTeam,
    manageAllCompanies: !!data?.permissions?.manageAllCompanies,
  }
});

const apiBase = import.meta.env.VITE_API_URL;

export const useTeamStore = defineStore('teamStore', () => {
  const notificationsStore = useNotificationsStore();
  const authStore = useAuthStore();

  const groups = ref<IGroup[]>([]);
  const users = ref<IUser[]>([]);

  const groupsMap = computed<Map<string, IGroup>>(() => new Map(groups.value.map(g => [g.id, g])));
  const usersMap = computed<Map<string, IUser>>(() => new Map(users.value.map(u => [u.id, u])));
  const groupUsersMap = computed<Map<string, IUser[]>>(() => {
    const result = new Map<string, IUser[]>();

    groups.value.forEach((g) => {
      result.set(g.id, []);
    });

    users.value.forEach((u) => {
      if (u.group && result.has(u.group)) {
        result.get(u.group)?.push(u);
      }
    });

    return result;
  });

  const teamFetched = ref<boolean>(false);
  const fetchTeam = ():Promise<void> => {
    return new Promise((resolve, reject) => {

      if (!teamFetched.value) {

        Promise.all([
          getDocs(collection(db, 'groups'))
            .then((docs) => {
              const newGroups:IGroup[] = [];
              docs.forEach((doc) => {
                const groupData:any = doc.data();
                newGroups.push(rawDataToGroup(groupData, doc.id));
              });

              groups.value = newGroups;
            })
            .catch(err => {
              console.error(err);
            }),
          getDocs(collection(db, 'users'))
            .then((docs) => {
              const newUsers:IUser[] = [];
              docs.forEach((doc) => {
                const userData:any = doc.data();
                newUsers.push(rawDataToUser(userData, doc.id));
              });

              users.value = newUsers;
            })
            .catch(err => {
              console.error(err);
            })
        ])
          .then(() => {
            teamFetched.value = true;
            resolve();
          })
          .catch((err) => {
            reject(err);
          });
      } else {
        resolve();
      }
    });
  };

	

  const fetchGroup = (id: string):Promise<void> => {
    return new Promise((resolve, reject) => {
      getDoc(doc(db, '/groups/' + id))
        .then((doc) => {
          if (doc.data()) {
            const newGroup:IGroup = rawDataToGroup(doc.data(), doc.id);
            const newGroups = groups.value;

            if (groupsMap.value.has(id)) {
              const indexOfGroup:number = newGroups.findIndex(g => g.id === id);

              if (indexOfGroup > -1) {
                newGroups[indexOfGroup] = newGroup;
              }
            } else {
              newGroups.push(newGroup);
            }

            groups.value = [...newGroups];

            resolve();
          } else {
            resolve();
          }
          
        })
        .catch(err => {
          console.error(err);
          reject(err);
        })
    });
  };

  const fetchUser = (id: string):Promise<IUser> => {
    return new Promise((resolve, reject) => {
      getDoc(doc(db, '/users/' + id))
        .then((doc) => {
          const newUser:IUser = rawDataToUser(doc.data(), doc.id);
          const newUsers = users.value;

          if (usersMap.value.has(id)) {
            const indexOfUser:number = newUsers.findIndex(g => g.id === id);

            if (indexOfUser > -1) {
              newUsers[indexOfUser] = newUser;
            }
          } else {
            newUsers.push(newUser);
          }

          users.value = [...newUsers];

          resolve(newUser);
        })
        .catch(err => {
          console.error(err);
          reject(err);
        })
    });
  };

  const createGroup = (group:IGroup):Promise<string> => {
    return new Promise((resolve, reject) => {

      axios<string>({
        method: 'POST',
        url: apiBase + `/groups/`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: group
      })
        .then((result) =>  {
          fetchGroup(result.data)
            .then(() => {
              resolve(result.data);
            })
            .catch(err => {
              console.error(err);
              reject(err);
            });
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Create Company Failed');
          reject(err);
        });
    });
  };

  const editGroup = (id:string, group:IGroup):Promise<void> => {
    return new Promise((resolve, reject) => {

      axios({
        method: 'PUT',
        url: apiBase + `/groups/${id}`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: group
      })
        .then(() =>  {
          fetchGroup(id)
            .then(() => {
              resolve();
            })
            .catch(err => {
              console.error(err);
              reject(err);
            });
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Edit Company Failed');
          reject(err);
        });
    });
  };

  const activateGroup = (id:string):Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'PUT',
        url: apiBase + `/groups/${id}/activate`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        }
      })
        .then(() =>  {
          fetchGroup(id)
            .then(() => {
              resolve();
              console.log(`Group ${id} re-activated!`);
            })
            .catch(err => {
              console.error(err);
              reject(err);
            });
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Activate Company Failed');
          reject(err);
        });
    });
  };

  const deactivateGroup = (id:string):Promise<void> => {
    return new Promise((resolve, reject) => {

      axios({
        method: 'PUT',
        url: apiBase + `/groups/${id}/deactivate`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        }
      })
        .then(() =>  {
          fetchGroup(id)
            .then(() => {
              resolve();
              console.log(`Group ${id} deactivated!`);
            })
            .catch(err => {
              console.error(err);
              reject(err);
            });
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Deactivate Company Failed');
          reject(err);
        });
    });
  };

  const toggleGroupDeviceOption = (groupId:string, value: DeviceType):Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      axios({
        method: 'PUT',
        url: apiBase + `/groups/${groupId}/deviceOption`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: { value },
      })
        .then(() =>  {
          fetchGroup(groupId)
            .then(() => {
              resolve();
            })
            .catch(err => {
              console.error(err);
              reject(err);
            });
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Toggle Device Option Failed');
          reject(err);
        });
    });
  };

  const createUser = (user:IUser, password: string):Promise<string> => {
    return new Promise((resolve, reject) => {
      axios<string>({
        method: 'POST',
        url: apiBase + `/users`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: {...user, password},
      })
        .then((result) =>  {
          fetchUser(result.data)
            .then(() => {
              resolve(result.data);
            })
            .catch(err => {
              console.error(err);
              reject(err);
            });
        })
        .catch((err) => {
          if (err?.response?.data === 'taken') {
            notificationsStore.error('Email Already Registered');
            reject('taken');
          } else {
            console.error(err);
            notificationsStore.error('Create User Failed');
            reject(err);
          }
        });
    });
  };

  const editUser = (id:string, user:IUser):Promise<void> => {
    return new Promise((resolve, reject) => {

      axios({
        method: 'PUT',
        url: apiBase + `/users/${id}`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: user,
      })
        .then(() =>  {
          fetchUser(id)
            .then(() => {
              resolve();
            })
            .catch(err => {
              console.error(err);
              reject(err);
            });
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Edit User Failed');
          reject(err);
        });
    });
  };

  const sendEditEmailCode = (uid:string, email: string):Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'PUT',
        url: apiBase + `/users/${uid}/edit-email-code`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: {
          email,
        }
      })
        .then(() =>  {
          resolve();
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Error');
          reject(err);
        });
    });
  };

  const editUserEmail = (uid:string, email:string, code: string):Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'PUT',
        url: apiBase + `/users/${uid}/email`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: {
          email,
          code
        },
      })
        .then(() =>  {
          fetchUser(uid)
            .then(() => {
              resolve();
            })
            .catch(err => {
              console.error(err);
              reject(err);
            });
        })
        .catch((err) => {
          if (err?.response?.data === 'invalid code') {
            notificationsStore.error('Wrong Code');
            reject('taken');
          } else {
            console.error(err);
            notificationsStore.error('Edit User Failed');
            reject(err);
          }
        });
    });
  };

  const toggleUserPermission = (uid:string, permission:'analytics' | 'admin', value:boolean):Promise<void> => {
    return new Promise<void>((resolve, reject) => {

      const savedValue = usersMap.value.get(uid)?.permissions[permission];
      usersMap.value.get(uid)!.permissions[permission] = value;

      axios({
        method: 'PUT',
        url: apiBase + `/users/${uid}/permissions`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: {
          permission,
          value
        },
      })
        .then(() =>  {
          fetchUser(uid)
            .then(() => {
              resolve();
            })
            .catch(err => {
              console.error(err);
              reject(err);
            });
        })
        .catch((err) => {
          usersMap.value.get(uid)!.permissions[permission] = savedValue;
          console.error(err);
          notificationsStore.error('Toggle Permission Failed');
          reject(err);
        });
    });
  };

  const activateUser = (id:string):Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'PUT',
        url: apiBase + `/users/${id}/activate`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        }
      })
        .then(() =>  {
          fetchUser(id)
            .then(() => {
              resolve();
              console.log(`User ${id} re-activated!`);
            })
            .catch(err => {
              console.error(err);
              reject(err);
            });
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Activate User Failed');
          reject(err);
        });
    });
  };

  const deactivateUser = (id:string):Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'PUT',
        url: apiBase + `/users/${id}/deactivate`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        }
      })
        .then(() =>  {
          fetchUser(id)
            .then(() => {
              resolve();
            })
            .catch(err => {
              console.error(err);
              reject(err);
            });
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Deactivate User Failed');
          reject(err);
        });
    });
  };

  const sendEditPasswordCode = (uid:string):Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'PUT',
        url: apiBase + `/users/${uid}/edit-password-code`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
      })
        .then(() =>  {
					console.log('Code sent successfully');
          resolve();
        })
        .catch((err) => {
          console.error(err);
					console.error('Error sending code:', err);
          notificationsStore.error('Error');
          reject(err);
        });
    });
  };

  const resetUserPassword = (id:string, password:string, code?: string):Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'PUT',
        url: apiBase + `/users/${id}/password`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: { password, code: code || '' }
      })
        .then(() =>  {
          resolve();
        })
        .catch((err) => {
          if (err?.response?.data === 'invalid code') {
            notificationsStore.error('Wrong Code');
            reject('taken');
          } else {
            console.error(err);
            notificationsStore.error('Reset User Password Failed');
            reject(err);
          }
        });
    });
  };

  return { groups, users, activateUser, deactivateUser, createUser, toggleGroupDeviceOption, resetUserPassword, groupsMap, fetchUser, editUser, fetchTeam, usersMap, toggleUserPermission, groupUsersMap, fetchGroup, createGroup, editGroup, activateGroup, deactivateGroup, sendEditEmailCode, editUserEmail, sendEditPasswordCode };
});